import { RefreshCw, ArrowRightLeft, AlertTriangle, ListChecks, GitBranch, Radar } from "lucide-react";
import type { MoneyPageContent } from "./types";

/**
 * Rebranding / migration SEO service page.
 * Focused on protecting organic search equity during a domain change,
 * rebrand, replatform or site redesign.
 */
export const rebrandingSeoPage: MoneyPageContent = {
  id: "rebranding-seo",
  icon: RefreshCw,
  serviceType: "SEO Migration and Rebranding",
  esPath: "/servicios/rebranding-seo",
  enPath: "/en/services/rebranding-seo",
  es: {
    metaTitle: "SEO en Rebranding y Migraciones | Proteger tu tráfico",
    metaDescription:
      "SEO para rebranding, cambio de dominio o rediseño: inventario previo, mapeo de URLs, redirecciones y monitoreo para no perder el tráfico orgánico acumulado.",
    keywords: [
      "seo rebranding",
      "migración de sitio web seo",
      "cambio de dominio seo",
      "redirecciones 301",
      "seo rediseño web",
    ],
    h1: "SEO para rebranding y migraciones: proteger lo que ya construiste",
    breadcrumbLabel: "Rebranding y Migraciones",
    heroSubtitle: "Cambiar de marca sin perder el tráfico que te costó años ganar",
    heroDescription:
      "Un rebranding, cambio de dominio, replataformización o rediseño puede tirar por la borda años de posicionamiento en semanas si se ejecuta sin un plan SEO. Trabajo el inventario previo, el mapeo de URLs, la estrategia de redirecciones y el monitoreo posterior para que el cambio de marca no se traduzca en pérdida de visibilidad.",
    ctaPrimary: {
      label: "Solicitar diagnóstico estratégico",
      to: "/diagnostico-seo-gratis",
    },
    ctaSecondary: { label: "Ver casos documentados", to: "/casos-de-exito" },
    summary: [
      {
        label: "Inventario previo",
        text: "Qué URLs, páginas y señales de autoridad existen hoy y cuáles no se pueden perder en el cambio.",
      },
      {
        label: "Mapeo de URLs y redirecciones",
        text: "Correspondencia 1 a 1 entre la estructura antigua y la nueva, con redirecciones planificadas, no improvisadas.",
      },
      {
        label: "QA por etapas",
        text: "Validación en staging antes del lanzamiento, no solo revisión después de que el sitio ya está en vivo.",
      },
      {
        label: "Monitoreo y criterios de rollback",
        text: "Seguimiento post-lanzamiento con umbrales claros que definen cuándo revertir un cambio.",
      },
    ],
    explanation: {
      title: "Qué implica el SEO en un rebranding o migración (y qué no es)",
      definition:
        "El SEO en rebranding y migraciones es el trabajo de planificación y validación técnica que asegura que un cambio de dominio, plataforma, estructura de URLs o identidad de marca conserve el valor de posicionamiento acumulado. No es ejecutar el cambio y luego revisar qué pasó: es diseñar el cambio para que el impacto en visibilidad sea controlado y medible desde antes del lanzamiento.",
      whyImportant: {
        title: "Por qué es distinto a un lanzamiento normal",
        text: "Google trata cada cambio de URL, dominio o estructura como una señal a reinterpretar. Sin un mapeo claro y redirecciones correctas, el buscador puede tardar en reconocer que la nueva URL es la continuación de la anterior, o directamente no hacerlo.",
      },
      consequences: {
        title: "Qué pasa si se ignora",
        text: "Caídas de tráfico orgánico difíciles de revertir, páginas de alto valor sin redirigir, autoridad de dominio que no se transfiere, y semanas o meses de trabajo correctivo después del lanzamiento en lugar de antes.",
      },
    },
    problems: {
      title: "Riesgos que trabajo con más frecuencia en migraciones",
      subtitle: "Cada uno puede costar visibilidad si no se planifica con anticipación",
      items: [
        {
          icon: ListChecks,
          title: "Inventario incompleto antes del cambio",
          description:
            "Se migra sin tener un registro completo de URLs indexadas, páginas que reciben tráfico o enlaces entrantes relevantes.",
          impact: "URLs valiosas que desaparecen sin que nadie lo note hasta después.",
        },
        {
          icon: ArrowRightLeft,
          title: "Mapeo de URLs improvisado",
          description:
            "Redirecciones genéricas hacia la página de inicio o hacia categorías que no corresponden al contenido original.",
          impact: "Pérdida de relevancia y señales de autoridad diluidas en páginas equivocadas.",
        },
        {
          icon: GitBranch,
          title: "Cadenas de redirección y errores 404",
          description:
            "Redirecciones encadenadas, en bucle o simplemente ausentes, generando errores que ni usuarios ni buscadores pueden resolver.",
          impact: "Presupuesto de rastreo desperdiciado y experiencia de usuario rota.",
        },
        {
          icon: AlertTriangle,
          title: "Lanzamiento sin QA en staging",
          description:
            "Cambios de contenido, metadatos, robots.txt o canonicalización que se validan directamente en producción.",
          impact: "Errores críticos (como bloqueos de indexación) que quedan en vivo por días.",
        },
        {
          icon: Radar,
          title: "Falta de monitoreo post-lanzamiento",
          description:
            "No hay seguimiento estructurado de indexación, rastreo y posiciones en las semanas posteriores al cambio.",
          impact: "Problemas que se detectan tarde, cuando ya hay pérdida de tráfico acumulada.",
        },
      ],
    },
    methodology: {
      title: "Cómo abordo una migración o rebranding",
      subtitle: "Planificación antes del lanzamiento, no reacción después",
      steps: [
        {
          number: "01",
          title: "Inventario y priorización previa",
          description:
            "Registro completo de URLs indexadas, tráfico, enlaces y palabras clave posicionadas, identificando qué páginas no pueden perderse en el cambio.",
        },
        {
          number: "02",
          title: "Mapeo de URLs y plan de redirecciones",
          description:
            "Correspondencia entre estructura antigua y nueva, definiendo redirecciones 301 específicas en lugar de reglas genéricas.",
        },
        {
          number: "03",
          title: "QA en staging antes del lanzamiento",
          description:
            "Validación de metadatos, canonicalización, robots.txt, sitemaps y redirecciones en un entorno de pruebas previo al cambio en producción.",
        },
        {
          number: "04",
          title: "Monitoreo posterior y criterios de rollback",
          description:
            "Seguimiento de indexación, rastreo y posiciones tras el lanzamiento, con umbrales definidos de antemano para decidir si algo debe revertirse.",
        },
      ],
      ctaText: "Solicitar diagnóstico estratégico",
      ctaLink: "/diagnostico-seo-gratis",
    },
    scope: {
      includes: {
        title: "Qué incluye este servicio",
        items: [
          "Inventario de URLs, tráfico y enlaces previo a la migración",
          "Mapeo de URLs y definición del plan de redirecciones",
          "Validación técnica en entorno de staging",
          "Coordinación con el equipo de desarrollo durante el lanzamiento",
          "Monitoreo de indexación y rastreo post-lanzamiento",
          "Definición de criterios de rollback",
        ],
      },
      notIncludes: {
        title: "Qué no incluye",
        items: [
          "Diseño de marca, naming o identidad visual",
          "Desarrollo o migración técnica del sitio en sí",
          "Estrategia de contenido nuevo (ver contenido y conversión)",
          "Garantía de que no habrá ninguna fluctuación temporal de tráfico",
        ],
      },
    },
    forWho: {
      idealFor: {
        title: "Cuándo aplica este servicio",
        items: [
          "Empresas planificando un rebranding con cambio de dominio o nombre",
          "Sitios que van a replataformizar (cambio de CMS o tecnología)",
          "Rediseños que modifican estructura de URLs, navegación o arquitectura",
          "Fusiones o consolidación de varios dominios en uno solo",
        ],
      },
      notFor: {
        title: "Cuándo no aplica",
        items: [
          "Cambios cosméticos que no tocan URLs, dominio ni estructura",
          "Sitios sin tráfico orgánico relevante que proteger",
          "Migraciones ya ejecutadas sin posibilidad de ajustar el plan previo (aunque sí se puede intervenir en la fase de corrección)",
        ],
      },
    },
    faq: {
      title: "Preguntas frecuentes",
      items: [
        {
          question: "¿Cuánto tráfico se pierde normalmente en una migración?",
          answer:
            "Depende del tamaño del sitio, la calidad del plan de redirecciones y la ejecución técnica. Una migración bien planificada busca minimizar la pérdida, pero no existe una migración sin ningún riesgo. No doy cifras porque dependen de cada caso.",
        },
        {
          question: "¿Cuándo debo empezar a trabajar el SEO de la migración?",
          answer:
            "Idealmente antes de que se tome la decisión final de estructura de URLs o plataforma. Cuanto antes se involucre el SEO en el proceso, más margen hay para evitar decisiones que luego son costosas de corregir.",
        },
        {
          question: "¿Ya migramos y ahora vemos caídas de tráfico, pueden ayudar igual?",
          answer:
            "Sí. Se puede hacer un diagnóstico de lo que ocurrió, revisar redirecciones, indexación y señales técnicas, y definir un plan de corrección. No es lo ideal, pero sí es una situación que se trabaja con frecuencia.",
        },
        {
          question: "¿Qué pasa si cambiamos de dominio pero mantenemos la misma estructura de URLs?",
          answer:
            "Sigue siendo una migración de riesgo. El cambio de dominio en sí mismo requiere redirecciones y reevaluación por parte de los buscadores, aunque la estructura interna se mantenga igual.",
        },
        {
          question: "¿Necesito pausar el sitio o el marketing durante la migración?",
          answer:
            "No necesariamente. El objetivo del plan es justamente que el lanzamiento se pueda ejecutar con normalidad, con validaciones previas que reducen la necesidad de pausar actividad.",
        },
      ],
    },
    related: {
      title: "Servicios relacionados",
      subtitle: "Una migración exitosa suele apoyarse en otras áreas del sitio",
      links: [
        {
          label: "SEO Técnico",
          description: "Arquitectura, rastreo e indexación como base de cualquier migración.",
          to: "/servicios/seo-tecnico",
        },
        {
          label: "Auditoría SEO profesional",
          description: "Diagnóstico completo del estado del sitio antes o después de un cambio.",
          to: "/servicios/seo-diagnostico",
        },
        {
          label: "Consultoría SEO",
          description: "Acompañamiento estratégico durante todo el proceso de cambio de marca.",
          to: "/consultor-seo",
        },
      ],
    },
    llmSummary:
      "Servicio de SEO enfocado en proteger el tráfico orgánico durante rebranding, cambios de dominio, replataformización o rediseños, mediante inventario previo, mapeo de URLs, redirecciones, QA en staging y monitoreo post-lanzamiento con criterios de rollback.",
    keyPoints: [
      "Se centra en preservar el valor SEO acumulado durante un cambio de marca o plataforma",
      "Incluye inventario, mapeo de URLs, redirecciones, QA por etapas y monitoreo posterior",
      "Aplica a rebranding, cambio de dominio, replataformización, rediseño y fusiones de dominios",
      "No sustituye el trabajo de diseño de marca ni el desarrollo técnico del sitio",
    ],
  },
  en: {
    metaTitle: "SEO for Rebranding & Migrations | Protect Your Traffic",
    metaDescription:
      "SEO for rebrands, domain changes and replatforming: pre-migration inventory, URL mapping, redirects and post-launch monitoring to protect organic visibility.",
    keywords: [
      "seo migration",
      "rebranding seo",
      "domain change seo",
      "301 redirects strategy",
      "website redesign seo",
    ],
    h1: "SEO for rebranding and migrations: protect what you already built",
    breadcrumbLabel: "Rebranding & Migrations",
    heroSubtitle: "Change your brand without losing the traffic you spent years earning",
    heroDescription:
      "A rebrand, domain change, replatforming project or redesign can undo years of search visibility in a matter of weeks if it's executed without an SEO plan. I work on the pre-migration inventory, URL mapping, redirect strategy and post-launch monitoring so the brand change doesn't turn into a visibility loss.",
    ctaPrimary: {
      label: "Review My Migration Risk",
      to: "/en/free-seo-diagnosis",
    },
    ctaSecondary: { label: "See documented work", to: "/en/case-studies" },
    summary: [
      {
        label: "Pre-migration inventory",
        text: "Which URLs, pages and authority signals exist today, and which ones can't be lost in the change.",
      },
      {
        label: "URL mapping & redirects",
        text: "A one-to-one match between the old and new structure, with redirects planned, not improvised.",
      },
      {
        label: "Staged QA",
        text: "Validation in staging before launch, not just a review after the site is already live.",
      },
      {
        label: "Monitoring & rollback criteria",
        text: "Structured post-launch tracking with clear thresholds that define when to reverse a change.",
      },
    ],
    explanation: {
      title: "What SEO for rebranding and migrations involves (and what it isn't)",
      definition:
        "SEO for rebranding and migrations is the planning and technical validation work that ensures a domain change, platform switch, URL restructuring or brand identity change preserves the search equity you've already built. It's not executing the change and then checking what happened afterward — it's designing the change so the impact on visibility is controlled and measurable before launch.",
      whyImportant: {
        title: "Why it's different from a regular launch",
        text: "Google treats every URL, domain or structure change as a signal it has to reinterpret. Without clear mapping and correct redirects, it can take time for a search engine to recognize the new URL as the continuation of the old one — or it may not make that connection at all.",
      },
      consequences: {
        title: "What happens if it's ignored",
        text: "Organic traffic drops that are hard to reverse, high-value pages left without redirects, domain authority that doesn't carry over, and weeks or months of corrective work after launch instead of prevention beforehand.",
      },
    },
    problems: {
      title: "Risks I work with most often in migrations",
      subtitle: "Each one can cost visibility if it isn't planned in advance",
      items: [
        {
          icon: ListChecks,
          title: "Incomplete pre-migration inventory",
          description:
            "Migrating without a full record of indexed URLs, pages receiving traffic, or relevant inbound links.",
          impact: "Valuable URLs that disappear without anyone noticing until it's too late.",
        },
        {
          icon: ArrowRightLeft,
          title: "Improvised URL mapping",
          description:
            "Generic redirects pointed at the homepage or at categories that don't correspond to the original content.",
          impact: "Lost relevance and authority signals diluted onto the wrong pages.",
        },
        {
          icon: GitBranch,
          title: "Redirect chains and 404 errors",
          description:
            "Chained redirects, redirect loops, or missing redirects entirely, creating errors neither users nor search engines can resolve.",
          impact: "Wasted crawl budget and a broken user experience.",
        },
        {
          icon: AlertTriangle,
          title: "Launching without staging QA",
          description:
            "Content, metadata, robots.txt or canonicalization changes validated directly in production instead of beforehand.",
          impact: "Critical errors, like accidental indexing blocks, staying live for days.",
        },
        {
          icon: Radar,
          title: "No post-launch monitoring",
          description:
            "No structured tracking of indexing, crawling and rankings in the weeks following the change.",
          impact: "Problems caught late, after traffic loss has already accumulated.",
        },
      ],
    },
    methodology: {
      title: "How I approach a migration or rebrand",
      subtitle: "Planning before launch, not reacting after",
      steps: [
        {
          number: "01",
          title: "Inventory and prioritization",
          description:
            "A complete record of indexed URLs, traffic, links and ranking keywords, identifying which pages can't afford to be lost in the change.",
        },
        {
          number: "02",
          title: "URL mapping and redirect plan",
          description:
            "Matching the old structure to the new one, defining specific 301 redirects rather than generic catch-all rules.",
        },
        {
          number: "03",
          title: "Staging QA before launch",
          description:
            "Validating metadata, canonicalization, robots.txt, sitemaps and redirects in a test environment before the change goes live.",
        },
        {
          number: "04",
          title: "Post-launch monitoring and rollback criteria",
          description:
            "Tracking indexing, crawling and rankings after launch, with thresholds defined ahead of time to decide whether something needs to be reversed.",
        },
      ],
      ctaText: "Review My Migration Risk",
      ctaLink: "/en/free-seo-diagnosis",
    },
    scope: {
      includes: {
        title: "What this service includes",
        items: [
          "URL, traffic and link inventory prior to migration",
          "URL mapping and redirect plan definition",
          "Technical validation in a staging environment",
          "Coordination with the development team during launch",
          "Post-launch indexing and crawl monitoring",
          "Defined rollback criteria",
        ],
      },
      notIncludes: {
        title: "What it doesn't include",
        items: [
          "Brand design, naming or visual identity work",
          "The actual technical development or migration of the site",
          "New content strategy (see search content and conversion)",
          "A guarantee that there will be zero temporary traffic fluctuation",
        ],
      },
    },
    forWho: {
      idealFor: {
        title: "When this applies",
        items: [
          "Companies planning a rebrand that involves a domain or name change",
          "Sites replatforming to a new CMS or technology stack",
          "Redesigns that change URL structure, navigation or architecture",
          "Mergers or consolidation of multiple domains into one",
        ],
      },
      notFor: {
        title: "When it doesn't apply",
        items: [
          "Cosmetic changes that don't touch URLs, domain or structure",
          "Sites without meaningful organic traffic to protect",
          "Migrations already executed with no room to adjust the original plan (though corrective work is still possible after the fact)",
        ],
      },
    },
    faq: {
      title: "Frequently asked questions",
      items: [
        {
          question: "How much traffic is typically lost during a migration?",
          answer:
            "It depends on the size of the site, the quality of the redirect plan and how the technical execution goes. A well-planned migration aims to minimize loss, but no migration is entirely risk-free. I don't quote figures because every case is different.",
        },
        {
          question: "When should I start SEO work for the migration?",
          answer:
            "Ideally before the final decision on URL structure or platform is made. The earlier SEO gets involved in the process, the more room there is to avoid decisions that are expensive to fix later.",
        },
        {
          question: "We already migrated and we're seeing traffic drops — can you still help?",
          answer:
            "Yes. I can diagnose what happened, review redirects, indexing and technical signals, and define a correction plan. It's not the ideal scenario, but it's one I work with often.",
        },
        {
          question: "What if we change domains but keep the same URL structure?",
          answer:
            "It's still a risky migration. The domain change itself requires redirects and re-evaluation by search engines, even if the internal structure stays the same.",
        },
        {
          question: "Do we need to pause the site or marketing during the migration?",
          answer:
            "Not necessarily. The point of the plan is precisely to let the launch happen normally, with prior validation that reduces the need to pause activity.",
        },
      ],
    },
    related: {
      title: "Related services",
      subtitle: "A successful migration usually depends on other areas of the site",
      links: [
        {
          label: "Technical SEO",
          description: "Architecture, crawling and indexing as the foundation of any migration.",
          to: "/en/services/technical-seo",
        },
        {
          label: "Professional SEO audit",
          description: "A full diagnostic of the site's state before or after a change.",
          to: "/en/services/seo-audit",
        },
        {
          label: "SEO consulting",
          description: "Strategic guidance throughout the entire rebranding process.",
          to: "/en/seo-consultant",
        },
      ],
    },
    llmSummary:
      "SEO service focused on protecting organic traffic during rebranding, domain changes, replatforming or redesigns, through pre-migration inventory, URL mapping, redirects, staged QA and post-launch monitoring with rollback criteria.",
    keyPoints: [
      "Focused on preserving accumulated SEO value during a brand or platform change",
      "Includes inventory, URL mapping, redirects, staged QA and post-launch monitoring",
      "Applies to rebrands, domain changes, replatforming, redesigns and domain consolidations",
      "Does not replace brand design work or the technical development of the site",
    ],
  },
};
